import * as THREE from 'three';

/** 手続きテクスチャで PBR 風マテリアルを生成 */
function noiseCanvas(size: number, base: string, variance: number, grain = 1): HTMLCanvasElement {
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = base;
  ctx.fillRect(0, 0, size, size);
  const img = ctx.getImageData(0, 0, size, size);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    const n = (Math.random() - 0.5) * variance * grain;
    d[i] = clamp(d[i] + n);
    d[i + 1] = clamp(d[i + 1] + n);
    d[i + 2] = clamp(d[i + 2] + n);
  }
  ctx.putImageData(img, 0, 0);
  return c;
}

function clamp(v: number): number {
  return Math.max(0, Math.min(255, v));
}

function texFromCanvas(c: HTMLCanvasElement, repeat = 1): THREE.CanvasTexture {
  const t = new THREE.CanvasTexture(c);
  t.wrapS = t.wrapT = THREE.RepeatWrapping;
  t.repeat.set(repeat, repeat);
  t.colorSpace = THREE.SRGBColorSpace;
  t.anisotropy = 4;
  return t;
}

function woodTexture(): THREE.CanvasTexture {
  const size = 256;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#4a3424';
  ctx.fillRect(0, 0, size, size);
  for (let y = 0; y < size; y++) {
    const shade = 40 + Math.sin(y * 0.15) * 12 + Math.random() * 10;
    ctx.fillStyle = `rgb(${80 + shade * 0.4},${50 + shade * 0.25},${30 + shade * 0.1})`;
    ctx.fillRect(0, y, size, 1);
    if (y % 17 === 0) {
      ctx.strokeStyle = `rgba(30,18,10,${0.15 + Math.random() * 0.2})`;
      ctx.beginPath();
      ctx.moveTo(0, y);
      for (let x = 0; x < size; x += 8) {
        ctx.lineTo(x, y + Math.sin(x * 0.05) * 3);
      }
      ctx.stroke();
    }
  }
  return texFromCanvas(c, 2);
}

function marbleTexture(): THREE.CanvasTexture {
  const size = 256;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#d8d2c8';
  ctx.fillRect(0, 0, size, size);
  for (let i = 0; i < 18; i++) {
    ctx.strokeStyle = `rgba(120,110,100,${0.15 + Math.random() * 0.2})`;
    ctx.lineWidth = 1 + Math.random() * 2;
    ctx.beginPath();
    let x = Math.random() * size;
    let y = 0;
    ctx.moveTo(x, y);
    while (y < size) {
      x += (Math.random() - 0.5) * 20;
      y += 8 + Math.random() * 10;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  return texFromCanvas(c, 1);
}

function wallpaperTexture(): THREE.CanvasTexture {
  const size = 256;
  const c = document.createElement('canvas');
  c.width = c.height = size;
  const ctx = c.getContext('2d')!;
  ctx.fillStyle = '#2a3038';
  ctx.fillRect(0, 0, size, size);
  ctx.strokeStyle = 'rgba(180,160,120,0.08)';
  ctx.lineWidth = 1;
  for (let i = 0; i < size; i += 32) {
    for (let j = 0; j < size; j += 32) {
      ctx.strokeRect(i + 4, j + 4, 24, 24);
      ctx.beginPath();
      ctx.arc(i + 16, j + 16, 4, 0, Math.PI * 2);
      ctx.stroke();
    }
  }
  return texFromCanvas(c, 3);
}

function carpetTexture(): THREE.CanvasTexture {
  const c = noiseCanvas(128, '#3a2428', 30, 1.5);
  return texFromCanvas(c, 4);
}

function metalTexture(): THREE.CanvasTexture {
  const c = noiseCanvas(64, '#6a6e74', 25);
  return texFromCanvas(c, 1);
}

function stoneTexture(): THREE.CanvasTexture {
  const c = noiseCanvas(128, '#3a3e42', 40);
  return texFromCanvas(c, 2);
}

export interface MaterialSet {
  wood: THREE.MeshStandardMaterial;
  woodDark: THREE.MeshStandardMaterial;
  marble: THREE.MeshStandardMaterial;
  wallpaper: THREE.MeshStandardMaterial;
  wallpaperWarm: THREE.MeshStandardMaterial;
  carpet: THREE.MeshStandardMaterial;
  metal: THREE.MeshStandardMaterial;
  metalGold: THREE.MeshStandardMaterial;
  stone: THREE.MeshStandardMaterial;
  glass: THREE.MeshStandardMaterial;
  fabric: THREE.MeshStandardMaterial;
  plaster: THREE.MeshStandardMaterial;
  emissiveWarm: THREE.MeshStandardMaterial;
  emissiveCool: THREE.MeshStandardMaterial;
  painting: THREE.MeshStandardMaterial;
  door: THREE.MeshStandardMaterial;
}

export function createMaterials(): MaterialSet {
  const woodMap = woodTexture();
  const marbleMap = marbleTexture();
  const wallMap = wallpaperTexture();
  const carpetMap = carpetTexture();
  const metalMap = metalTexture();
  const stoneMap = stoneTexture();

  return {
    wood: new THREE.MeshStandardMaterial({
      map: woodMap,
      roughness: 0.72,
      metalness: 0.05,
      color: 0xffffff,
    }),
    woodDark: new THREE.MeshStandardMaterial({
      map: woodMap,
      roughness: 0.8,
      metalness: 0.04,
      color: 0x6a5040,
    }),
    marble: new THREE.MeshStandardMaterial({
      map: marbleMap,
      roughness: 0.35,
      metalness: 0.08,
    }),
    wallpaper: new THREE.MeshStandardMaterial({
      map: wallMap,
      roughness: 0.9,
      metalness: 0,
      color: 0xc8c4bc,
    }),
    wallpaperWarm: new THREE.MeshStandardMaterial({
      map: wallMap,
      roughness: 0.9,
      metalness: 0,
      color: 0xc4a888,
    }),
    carpet: new THREE.MeshStandardMaterial({
      map: carpetMap,
      roughness: 0.95,
      metalness: 0,
    }),
    metal: new THREE.MeshStandardMaterial({
      map: metalMap,
      roughness: 0.35,
      metalness: 0.85,
    }),
    metalGold: new THREE.MeshStandardMaterial({
      color: 0xc9a84c,
      roughness: 0.35,
      metalness: 0.9,
    }),
    stone: new THREE.MeshStandardMaterial({
      map: stoneMap,
      roughness: 0.88,
      metalness: 0.05,
    }),
    glass: new THREE.MeshStandardMaterial({
      color: 0xaaccff,
      metalness: 0.1,
      roughness: 0.05,
      transparent: true,
      opacity: 0.35,
      envMapIntensity: 1,
    }),
    fabric: new THREE.MeshStandardMaterial({
      color: 0x4a3a48,
      roughness: 0.95,
      metalness: 0,
    }),
    plaster: new THREE.MeshStandardMaterial({
      color: 0xb8b0a4,
      roughness: 0.92,
      metalness: 0,
    }),
    emissiveWarm: new THREE.MeshStandardMaterial({
      color: 0xffcc88,
      emissive: 0xffaa55,
      emissiveIntensity: 1.2,
      roughness: 0.4,
    }),
    emissiveCool: new THREE.MeshStandardMaterial({
      color: 0x88ccff,
      emissive: 0x4488ff,
      emissiveIntensity: 0.8,
      roughness: 0.4,
    }),
    painting: new THREE.MeshStandardMaterial({
      color: 0x2a4060,
      roughness: 0.7,
      metalness: 0.05,
    }),
    door: new THREE.MeshStandardMaterial({
      map: woodMap,
      roughness: 0.65,
      metalness: 0.1,
      color: 0x5a4030,
    }),
  };
}

/** キャンバスで絵画テクスチャ */
export function createPaintingTexture(): THREE.CanvasTexture {
  const c = document.createElement('canvas');
  c.width = 512;
  c.height = 384;
  const ctx = c.getContext('2d')!;
  // 夕景（視認性を確保した明るめトーン）
  const g = ctx.createLinearGradient(0, 0, 0, 384);
  g.addColorStop(0, '#3a4a78');
  g.addColorStop(0.4, '#d07048');
  g.addColorStop(0.65, '#f0b878');
  g.addColorStop(1, '#4a3830');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, 512, 384);
  // シルエット館
  ctx.fillStyle = '#1a1210';
  ctx.fillRect(160, 200, 200, 120);
  ctx.fillRect(200, 140, 40, 60);
  ctx.fillRect(300, 160, 30, 40);
  // 窓の灯り
  ctx.fillStyle = '#ffcc66';
  ctx.fillRect(220, 230, 18, 22);
  ctx.fillRect(280, 230, 18, 22);
  // ローマ数字
  ctx.fillStyle = 'rgba(255,245,220,0.95)';
  ctx.font = 'bold 48px serif';
  ctx.textAlign = 'center';
  ctx.fillText('III  :  XV', 256, 78);
  // 小さな数字 48
  ctx.font = 'bold 22px serif';
  ctx.fillStyle = 'rgba(255,240,200,0.95)';
  ctx.fillText('48', 455, 355);
  const t = new THREE.CanvasTexture(c);
  t.colorSpace = THREE.SRGBColorSpace;
  return t;
}
